import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';

import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Services = () => {
  const [teams, setTeams] = useState([]);
  const [department, setDepartment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    department: '',
    service_name: '',
    description: '',
    short_description: '',
    orderNumber: '',
    image: '',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php','services'); // Fetch services data
        if (response.success) {
          setTeams(response.data);
        } else {
          toast.error(response.message || "Failed to load service data");
        }

        const responseDepertment = await getData('proccess.php','department'); // Fetch services data
        if (responseDepertment.success) {
          setDepartment(responseDepertment.data);
        } else {
          toast.error(responseDepertment.message || "Failed to load service data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch services");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      department: '',
      service_name: '',
      description: '',
      short_description: '',
      orderNumber: '',
      image: ''
    });
    setOpenAddDialog(true);
  };

  // ✅ REORDER FUNCTION (updates backend immediately)
    const moveItem = async (index, direction) => {
      const newList = [...teams];
  
      if (direction === "up" && index === 0) return;
      if (direction === "down" && index === newList.length - 1) return;
  
      const swapIndex = direction === "up" ? index - 1 : index + 1;
  
      [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
  
      const reordered = newList.map((item, i) => ({
        ...item,
        orderNumber: i + 1,
      }));
  
      setTeams(reordered);
  
      // 🔥 update backend one by one (safe with your existing API)
      try {
        for (let item of reordered) {
          await updateData('proccess.php', 'services', {
            ...item,
            orderNumber: item.orderNumber
          }, item.id);
        }
        toast.success("Order updated");
      } catch (err) {
        toast.error("Failed to update order");
      }
    };

  const handleEditClick = (team) => {
    setSelectedTeam(team);
    setFormData({
      department: team.department || '',
      service_name: team.service_name || '',
      description: team.description || '',
      short_description: team.short_description || '',
      orderNumber: team.orderNumber || '',
      image: team.image || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (team) => {
    setTeamToDelete(team);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php','services', teamToDelete.id); // Delete service by ID
      if (response.success) {
        toast.success(`Service deleted successfully`);
        setTeams(teams.filter(team => team.id !== teamToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete service");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete service");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setTeamToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedTeam) {
        // Update existing service
        response = await updateData('proccess.php','services',  formData, selectedTeam.id); // Update service
        if (response.success) {
          toast.success(`Service updated successfully`);
          setTeams(response.data);
        }
      } else {
        // Add new service
        response = await addData('proccess.php','services', formData); // Add service
        if (response.success) {
          toast.success(`Service added successfully`);
          setTeams(response.data);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedTeam(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "orderNumber", headerName: "Order Number", flex: 0.5, minWidth: 20 },
    // ✅ REORDER COLUMN
    {
          field: "reorder",
          headerName: "Reorder",
          width: 100,
          sortable: false,
          renderCell: (params) => {
            const index = teams.findIndex(t => t.id === params.row.id);
    
            return (
              <Box display="flex" flexDirection="column">
                <Button size="small" onClick={() => moveItem(index, "up")} disabled={index === 0}>↑</Button>
                <Button size="small" onClick={() => moveItem(index, "down")} disabled={index === teams.length - 1}>↓</Button>
              </Box>
            );
          },
        },
    { field: "department", headerName: "Department", flex: 0.5, minWidth: 120 },
    { field: "service_name", headerName: "Service Name", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    { field: "short_description", headerName: "Short Description", flex: 1, minWidth: 120 },
    { 
      field: "image", 
      headerName: "Service Image", 
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Module"}
              style={{ width: 50, height: 50, objectFit: "cover", cursor: 'pointer' }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton 
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img 
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedTeam(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{selectedTeam ? "Edit Service" : "Add New Service"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField 
                select
                style={{ width: "100%" }}
                id="department"
                name="department"
                label="Department"
                variant="standard"
                value={formData.department}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {department.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                   {dep.department_name}
                  </option>
                ))}
              </TextField>
              <TextField
                style={{width: "100%"}}
                id="service_name"
                name="service_name"
                label="Service Name"
                variant="standard"
                value={formData.service_name}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="description"
                name="description"
                label="Description"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="short_description"
                name="short_description"
                label="Short Description"
                multiline
                minRows={4}
                maxRows={8}
                variant="standard"
                value={formData.short_description}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="orderNumber"
                name="orderNumber"
                label="Order Number"
                variant="standard"
                value={formData.orderNumber}
                onChange={handleFormChange}
                required
              />

              <Box width={"100%"}>
                <label>Service Image</label>
                <Input 
                  type="file" 
                  style={{width: "100%"}} 
                  id="image" 
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedTeam}
                />
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            sx={{backgroundColor: colors.blue[400]}} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedTeam ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{teamToDelete?.service_name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm}
            sx={{backgroundColor: colors.red[400]}} 
            variant="contained" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
          <Box flex={1}>
              <Typography
                  variant="h4"
                  color={colors.dark[400]}
                  fontWeight={"bold"}
                  sx={{mb: "5px"}}
              >Services Management</Typography>
              <Typography variant="h5" color={colors.blue[400]}>Manage offered services</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Service
          </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor = {colors.dark[300]} padding={".2rem"} mt={"1rem"}>
          <DataGrid
            rows={[...teams].sort((a, b) => a.orderNumber - b.orderNumber)}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            sx={{
                border: "none",
                ".MuiDataGrid-cell": {
                borderBottom: "none",
                backgroundColor: "none"
                },
                ".MuiDataGrid-toolbarContainer": {
                padding: "10px",
                backgroundColor: colors.primary[900],
                borderBottom: "none",
                },
                ".MuiDataGrid-quickFilter": {
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${colors.primary[100]}`,
                },
                ".MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.primary[900],
                },
                ".MuiDataGrid-footerContainer": {
                  backgroundColor: colors.primary[900],
                },
            }}
            slots={{
              toolbar: () => <GridToolbarQuickFilter />,
              noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
                toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                    debounceMs: 500,
                    placeholder: "Search...",
                },
                },
            }}
          />
      </Box>
    </Box>
  );
};

export default Services;